import type { PopoverContentProps } from "@radix-ui/react-popover";
import type { FinancialsSheet } from "components/financials/modern/types";
import { Popover, PopoverContent } from "components/ui/Popover";
import classNames from "components/ui/classNames";
import { MoveUpRightIcon, PartyPopperIcon } from "lucide-react";
import useTheme from "components/ui/useTheme";
import type { ReactNode } from "react";

interface ReportDateContextPopoverProps {
  side?: PopoverContentProps["side"];
  sideOffset?: PopoverContentProps["sideOffset"];
  children: ReactNode;
  context: NonNullable<FinancialsSheet["context"][number]>;
}
export default function ReportDateContextPopover({
  side = "bottom",
  sideOffset = 12,
  context,
  children,
}: ReportDateContextPopoverProps) {
  const theme = useTheme();

  if (context.__typename !== "FinancialsSheetSECContextItem") {
    return null;
  }

  return (
    <Popover>
      {children}

      <PopoverContent
        side={side}
        sideOffset={sideOffset}
        hideWhenDetached={true}
        avoidCollisions={true}
        onOpenAutoFocus={(event) => {
          // See: https://github.com/radix-ui/primitives/discussions/935
          event.preventDefault();
        }}
        className={classNames(
          "w-44 space-y-2 overflow-hidden rounded-xl border border-slate-300 p-2",
          "shadow-lg shadow-slate-900/10",
          {
            "border-slate-900/80 bg-slate-900 text-slate-300 shadow-neutral-950/40": theme === "dark",
            dark: theme === "dark",
          },
        )}
      >
        <div className="flex flex-col gap-y-2">
          <div className="flex flex-col gap-y-2">
            {context.linkToDetails && (
              <a
                className={classNames(
                  "flex w-full items-center justify-between rounded-md bg-indigo-100/50 p-2 text-sm text-indigo-600",
                  "hover:opacity-80",
                )}
                href={context.linkToDetails}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="font-bold">Link {context.formType}</span>
                <MoveUpRightIcon className="h-4 w-4" />
              </a>
            )}
            {context.linkToHighlights && (
              <a
                className={classNames(
                  "flex w-full items-center justify-between rounded-md bg-teal-700/10 p-2 text-sm text-teal-600",
                  "hover:opacity-80",
                )}
                href={context.linkToHighlights}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="font-bold">Highlights</span>
                <PartyPopperIcon className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
